import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";

import { ProjectGridItem } from "../../scenes/projects/ProjectGridItem";
import { GridLayout } from "./GridLayout";
import { ProjectListItem } from "../../scenes/projects/ProjectListItem";
import { ListLayout } from "./ListLayout";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { createElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme/theme";
const defaultLayouts = [
  {
    tooltip: "Grid",
    icon: <ViewModuleIcon />,
    Item: ProjectGridItem,
    layout: GridLayout,
  },
  {
    tooltip: "List",
    icon: <ViewListIcon />,
    Item: ProjectListItem,
    layout: ListLayout,
  },
];

export const LayoutShifter = ({
  items,
  label,
  layouts = defaultLayouts,
  header,
  actions = [],
  isLoading,
  onChange = () => {},
  onEdit = () => {},
  onLoading = () => {},
  sortOptions = [],
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [currentLayout, setCurrentLayout] = useState(0);
  const handleLayoutChange = (event, newCurrentLayout) => {
    if (newCurrentLayout !== null) setCurrentLayout(newCurrentLayout);
  };

  // sorting (only affects the displayed order; add/remove keep using `items`)
  const [sortIndex, setSortIndex] = useState(0);
  const sortedItems = useMemo(() => {
    if (!items || !sortOptions.length) return items;
    const compare = sortOptions[sortIndex] && sortOptions[sortIndex].compare;
    if (!compare) return items;
    return [...items].sort(compare);
  }, [items, sortOptions, sortIndex]);

  function handleRemovedItem(id) {
    var newItems = items.filter((obj) => obj.id !== id);
    onChange(newItems);
  }

  function handleAddItem(item) {
    onChange([...items, item]);
  }

  return (
    <Box className="w-full">
      <Box className="flex justify-between items-center mb-3">
        <Box className="flex gap-2 justify-start items-center">
          <Typography variant="h3" className="pr-4">
            {label}
          </Typography>
          {actions.map((action) => (
            <Button
              onClick={action.onClick}
              color={action.color || "primary"}
              variant={action.variant || "outlined"}
            >
              {action.label}
            </Button>
          ))}
        </Box>

        <Box className="flex gap-3 items-center">
          {sortOptions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: "160px" }}>
              <InputLabel id="layout-sort-label">{t("Sort by")}</InputLabel>
              <Select
                labelId="layout-sort-label"
                label={t("Sort by")}
                value={sortIndex}
                onChange={(e) => setSortIndex(e.target.value)}
              >
                {sortOptions.map((option, index) => (
                  <MenuItem key={option.key || index} value={index}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <ToggleButtonGroup
            value={currentLayout}
            exclusive
            onChange={handleLayoutChange}
            sx={{
              "& .Mui-selected svg": {
                color: colors.greenAccent[500],
              },
            }}
          >
            {layouts.map((layout, index) => (
              <ToggleButton value={index}>{layout.icon}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      {createElement(layouts[currentLayout].layout, {
        items: sortedItems,
        ItemComponent: layouts[currentLayout].Item,
        header,
        isLoading,
        onRemoved: handleRemovedItem,
        onAdd: handleAddItem,
        onEdit,
        onLoading,
      })}
    </Box>
  );
};
